import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {StorageService} from '@shared/services/storage/storage.service';
import {UUIDService} from '@shared/services/util/uuid.service';

export const LOGIN_REDIRECT_PATH = 'login-redirect';
export const LOGIN_REDIRECT_KEY_PARAM_NAME = 'peak_redirect_key';

const TARGET_URL_KEY_PREFIX = 'peak_redirect_url_';
const TARGET_URL_TTL_MS = 15 * 60 * 1000;

@Injectable({providedIn: 'root'})
export class LoginRedirectService {

  constructor(private location: Location, private storageService: StorageService, private uuidService: UUIDService) {}

  /**
   * The URI to hand Keycloak in place of targetUrl. Keycloak rejects any redirect_uri whose query holds an OIDC response
   * parameter name - code, state, iss, error and the rest - which the app's own URLs do, the Green Button Connect
   * provider callback among them. Only an opaque key travels in the query; targetUrl is stashed against it, keyed per
   * redirect so that logins started in separate tabs cannot take each other's target off the shared cookie fallback.
   */
  buildRedirectUri(targetUrl: string): string {
    const redirectKey = this.uuidService.generateUUID();
    this.storageService.session.set(TARGET_URL_KEY_PREFIX + redirectKey, targetUrl, TARGET_URL_TTL_MS, false);
    const redirectPath = '/' + LOGIN_REDIRECT_PATH + '?'
      + encodeURIComponent(LOGIN_REDIRECT_KEY_PARAM_NAME) + '=' + encodeURIComponent(redirectKey);
    return window.location.origin + this.location.prepareExternalUrl(redirectPath);
  }

  /**
   * As {@link buildRedirectUri} for the URL the browser is already on, or null on the login redirect route itself,
   * where swapping the URL would only add another hop through Keycloak.
   */
  buildRedirectUriForCurrentUrl(): string | null {
    // Location.path() reports the app root as the empty string rather than as '/'.
    const currentUrl = this.location.path(true) || '/';
    return currentUrl.startsWith('/' + LOGIN_REDIRECT_PATH) ? null : this.buildRedirectUri(currentUrl);
  }

  takeTargetUrl(redirectKey: string | null): string | null {
    return redirectKey ? this.storageService.session.pop(TARGET_URL_KEY_PREFIX + redirectKey) ?? null : null;
  }

}
